import { useRuntimeVariables } from 'app-kit'
import flatMap from 'lodash/flatMap'
import merge from 'lodash/merge'
import reduce from 'lodash/reduce'
import React, { useMemo } from 'react'
import { RolodexEntryPatchJson } from 'types'
import type { MemoizedFunction as LodashMemoizedFunction } from 'utils/lodashTypes'

type MemoizedFunction<Ret> = (() => Promise<Ret>) & LodashMemoizedFunction

export const DeleteCacheValue = 'deleted'
type DeleteCacheValue = typeof DeleteCacheValue

/** A context providing Rolodex-specific information */
export const RolodexContext = React.createContext<{
  apiURL: string
  // Maps
  entryCache: ReadonlyMap<string, RolodexEntryPatchJson | DeleteCacheValue>
  fileCache: ReadonlyMap<string, FileCacheValueType> // A mapping from entry ID and path to a function to get a URL for the file Blob
  // Setters
  setEntryCache: React.Dispatch<
    React.SetStateAction<
      ReadonlyMap<
        string,
        ReadonlyMap<string, RolodexEntryPatchJson | DeleteCacheValue>
      >
    >
  >
  setFileCache: React.Dispatch<
    React.SetStateAction<
      ReadonlyMap<string, ReadonlyMap<string, FileCacheValueType>>
    >
  >
}>({
  apiURL: '',
  entryCache: new Map(),
  setEntryCache: () => {},
  fileCache: new Map(),
  setFileCache: () => {},
})

export type FileCacheValueType = MemoizedFunction<string>

function createReadOnlyMap<K, V>(map?: Map<K, V>): ReadonlyMap<K, V> {
  return new Map(map)
}

/**
 * A high-level provider component which coordinates calls to Rolodex and
 * produces objects to represent unique entries in Rolodex. In the future,
 * advanced caching/editing might be supported via this provider.
 * @param children    Children of this component
 * @constructor
 */
export const RolodexProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const { ROLODEX_URL } = useRuntimeVariables()
  const [entryCache, setEntryCache] =
    React.useState(
      createReadOnlyMap<
        string,
        ReadonlyMap<string, RolodexEntryPatchJson | DeleteCacheValue>
      >()
    )
  const [fileCache, setFileCache] =
    React.useState(
      createReadOnlyMap<string, ReadonlyMap<string, FileCacheValueType>>()
    )

  // We flatten the hierarchical cache for code that accesses the cache
  // More recently instantiated sub-caches take precedence when merging entries
  const flatEntryCache = useMemo(() => {
    const allEntries = flatMap(Array.from(entryCache.values()), (subCache) =>
      Array.from(subCache.entries())
    )
    return reduce(
      allEntries,
      (accumulated, next) => {
        const [key, value] = next
        if (value === DeleteCacheValue) {
          accumulated.delete(key)
        } else {
          const accumulatedValue = accumulated.get(key)
          const merged = merge(accumulatedValue, value)
          accumulated.set(key, merged)
        }
        return accumulated
      },
      new Map<string, RolodexEntryPatchJson | DeleteCacheValue>()
    )
  }, [entryCache])
  const flatFileCache = useMemo(() => {
    const allEntries = flatMap(Array.from(fileCache.values()), (subCache) =>
      Array.from(subCache.entries())
    )
    return reduce(
      allEntries,
      (accumulated, next) => {
        const [key, value] = next
        accumulated.set(key, value)
        return accumulated
      },
      new Map<string, FileCacheValueType>()
    )
  }, [fileCache])

  return (
    <RolodexContext.Provider
      value={{
        apiURL: ROLODEX_URL,
        entryCache: flatEntryCache,
        fileCache: flatFileCache,
        setEntryCache,
        setFileCache,
      }}>
      {children}
    </RolodexContext.Provider>
  )
}
